import React, { useEffect, useMemo } from 'react'
import { FormProvider, useForm, useWatch } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'

import {
  Button,
  Select as ChakraSelect,
  Flex,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
} from '@chakra-ui/react'

import { Select } from '@/components/Form'

import { SectionHeader } from '@/components/Elements'

// Logic

//Constant
import { NormalTextCellRenderer } from '@/components/Table/components/Cell/ToolTipCell'
import EditableTable from '@/components/Table/EditableTable'
import { ApprovalStatus, ContributorStatus, OfferQuotationStatus, PRApprovalStatus } from '@/features/offers/constants'
import useOffer from '@/features/offers/hooks/useOffer'
import useOfferAction from '@/features/offers/hooks/useOfferAction'
import useUser from '@/features/users/useUser'
import { hideAdminEditOfferModal, selectAdminEditOfferModal } from '@/stores/offers/offersSlice'
import { paddingZeroString } from '@/utils/stringHelper'
import { GCPRStatus, OfferBookingStatus, PRStatus } from '@eapproval/offer-shared'
import { createColumnHelper } from '@tanstack/react-table'

const AdminEditOfferModal = () => {
  const { show, offerId } = useSelector(selectAdminEditOfferModal)

  // Hook
  const { useFetchOffer } = useOffer()
  const { useAdminEditOffer } = useOfferAction()
  const { useFetchAllUsers } = useUser()
  const adminEditOffer = useAdminEditOffer()
  const { data: offerDetail, error, isLoading, isError } = useFetchOffer(offerId)
  const { data: allUsersResponse } = useFetchAllUsers(
    {
      page: 1,
      pageSize: 0,
      sortBy: 'fullName',
      orderBy: 1,
    },
    '',
    {
      enabled: !!show,
    }
  )

  const methods = useForm()
  const dispatch = useDispatch()
  const columnHelper = createColumnHelper()

  const contributorTableData = useWatch({ name: 'contributorDetails', control: methods.control })
  const optionalContributorTableData = useWatch({ name: 'optionalContributorDetails', control: methods.control })
  const quotationApproverTableData = useWatch({ name: 'quotationApproverDetails', control: methods.control })
  const bookingApproverTableData = useWatch({ name: 'bookingApproverDetails', control: methods.control })
  const gcprSubmissionTableData = useWatch({ name: 'gcprSubmissionTableData', control: methods.control })
  const prSubmissionTableData = useWatch({ name: 'prSubmissionTableData', control: methods.control })

  const allUsers = useMemo(() => {
    return Array.isArray(allUsersResponse?.data) ? allUsersResponse.data : []
  }, [allUsersResponse?.data])

  const { setValue } = methods

  const getDisplayUserName = (userDetail) => {
    if (!userDetail) {
      return ''
    }

    if (typeof userDetail === 'object') {
      if (userDetail.fullName) {
        return userDetail.fullName
      }

      const userId = userDetail._id
      return allUsers.find((user) => user?._id === userId)?.fullName || ''
    }

    return allUsers.find((user) => user?._id === userDetail)?.fullName || userDetail
  }

  useEffect(() => {
    if (offerDetail?.status) {
      setValue('status', offerDetail?.status)
    }
    if (offerDetail && Array.isArray(offerDetail?.contributors) && offerDetail?.contributors.length > 0) {
      setValue(
        'contributorDetails',
        offerDetail?.contributors.map((detail) => {
          return {
            tableType: 'contributor',
            role: detail.group,
            user: detail?.user?.fullName || '',
            status: detail.status || ContributorStatus.REQUIRED,
          }
        })
      )
    } else {
      setValue('contributorDetails', [])
    }

    if (
      offerDetail &&
      Array.isArray(offerDetail?.optionalContributors) &&
      offerDetail?.optionalContributors.length > 0
    ) {
      setValue(
        'optionalContributorDetails',
        offerDetail?.optionalContributors.map((detail) => {
          return {
            tableType: 'optionalContributor',
            role: detail.group,
            user: detail?.user?.fullName || '',
            status: detail.status || ContributorStatus.REQUIRED,
          }
        })
      )
    } else {
      setValue('optionalContributorDetails', [])
    }

    if (Array.isArray(offerDetail?.approvers) && offerDetail?.approvers?.length > 0) {
      setValue(
        'quotationApproverDetails',
        offerDetail?.approvers.map((detail) => {
          return {
            tableType: 'quotationApprover',
            role: `Level ${detail.level} ${detail.type}`,
            user: detail?.user?.fullName || '',
            status: detail.status || ApprovalStatus.UPCOMING,
          }
        })
      )
    } else {
      setValue('quotationApproverDetails', [])
    }

    if (Array.isArray(offerDetail?.booking?.approverDetails) && offerDetail?.booking?.approverDetails?.length > 0) {
      setValue(
        'bookingApproverDetails',
        offerDetail?.booking?.approverDetails.map((detail) => {
          return {
            tableType: 'bookingApprover',
            role: `Level ${detail.level} ${detail.type}`,
            user: detail?.user?.fullName || '',
            status: detail.status || ApprovalStatus.UPCOMING,
          }
        })
      )
    } else {
      setValue('bookingApproverDetails', [])
    }

    if (Array.isArray(offerDetail?.sourcing?.GCMaterialSubmissions)) {
      setValue(
        'gcprSubmissionTableData',
        offerDetail.sourcing.GCMaterialSubmissions.map((submission, submissionIndex) => {
          const approverDetails = Array.isArray(submission?.approverDetails)
            ? submission.approverDetails.map((detail) => {
                return {
                  tableType: 'gcprApprover',
                  role: detail?.level ? `Level ${detail.level} ${detail.type}` : detail?.type || '-',
                  user: getDisplayUserName(detail?.user),
                  status: detail?.status || ApprovalStatus.UPCOMING,
                }
              })
            : []

          return {
            status: submission?.status || GCPRStatus.DRAFT,
            submissionIndex,
            label: submission?.PRNumber || `Submission ${submissionIndex + 1}`,
            approverDetails,
          }
        })
      )
    } else {
      setValue('gcprSubmissionTableData', [])
    }

    if (Array.isArray(offerDetail?.sourcing?.costBudgets)) {
      setValue(
        'prSubmissionTableData',
        offerDetail.sourcing.costBudgets.flatMap((costBudget, costBudgetIndex) => {
          const vendorSelections = Array.isArray(costBudget?.PR?.vendorSelections) ? costBudget.PR.vendorSelections : []
          const RFQCode = costBudget?.detail?.RFQCode || '-'

          return vendorSelections.flatMap((vendorSelection, vendorSelectionIndex) => {
            const submissions = Array.isArray(vendorSelection?.submissions) ? vendorSelection.submissions : []

            return submissions.map((submission, submissionIndex) => {
              const approverDetails = Array.isArray(submission?.approverDetails)
                ? submission.approverDetails.map((detail) => {
                    return {
                      tableType: 'prSubmissionApprover',
                      role: detail?.level ? `Level ${detail.level} ${detail.type}` : detail?.type || '-',
                      user: getDisplayUserName(detail?.user),
                      status: detail?.status || PRApprovalStatus.UPCOMING,
                    }
                  })
                : []

              return {
                costBudgetIndex,
                vendorSelectionIndex,
                submissionIndex,
                label: `RFQ-${costBudgetIndex + 1}-V${vendorSelectionIndex + 1}-PR${paddingZeroString(submissionIndex + 1)}`,
                status: submission?.status || PRStatus.DRAFT,
                approverDetails,
              }
            })
          })
        })
      )
    } else {
      setValue('prSubmissionTableData', [])
    }
  }, [offerDetail, allUsers, setValue])

  const closeModal = () => {
    dispatch(hideAdminEditOfferModal())
  }

  const onSubmit = (data) => {
    if (!offerId || offerId === '') {
      toast.error('Missing Offer Id')
      return
    }

    adminEditOffer.mutate(
      { offerId, ...data },
      {
        onSuccess: () => {
          closeModal()
        },
      }
    )
  }

  const columnDef = useMemo(
    () => [
      columnHelper.accessor('role', {
        cell: NormalTextCellRenderer,
      }),
      columnHelper.accessor('user', {}),
      columnHelper.accessor('status', {
        cell: ({ row: { original, index }, column: { id: columnId }, getValue, table }) => {
          return (
            <ChakraSelect
              value={getValue() || ''}
              disabled={table?.options?.meta?.disabled}
              onChange={({ target: { value: newValue } }) => {
                table.options.meta?.updateData({ index, value: newValue })
              }}
            >
              {(original?.tableType === 'contributor' || original?.tableType === 'optionalContributor') &&
                Object.values(ContributorStatus).map((status) => {
                  return (
                    <option key={status} value={status}>
                      {status}
                    </option>
                  )
                })}
              {original?.tableType === 'prSubmissionApprover' &&
                Object.values(PRApprovalStatus).map((status) => {
                  return (
                    <option key={status} value={status}>
                      {status}
                    </option>
                  )
                })}
              {original?.tableType !== 'contributor' &&
                original?.tableType !== 'optionalContributor' &&
                original?.tableType !== 'prSubmissionApprover' &&
                Object.values(ApprovalStatus).map((status) => {
                  return (
                    <option key={status} value={status}>
                      {status}
                    </option>
                  )
                })}
            </ChakraSelect>
          )
        },
      }),
    ],
    [columnHelper]
  )

  const updateStatus = ({ type, index, value, submissionIndex }) => {
    switch (type) {
      case 'contributor':
        methods.setValue(`contributorDetails.${index}.status`, value, { shouldDirty: true })
        break
      case 'optionalContributor':
        methods.setValue(`optionalContributorDetails.${index}.status`, value, { shouldDirty: true })
        break
      case 'quotationApprover':
        methods.setValue(`quotationApproverDetails.${index}.status`, value, { shouldDirty: true })
        break
      case 'bookingApprover':
        methods.setValue(`bookingApproverDetails.${index}.status`, value, { shouldDirty: true })
        break
      case 'GCPRApproval':
        methods.setValue(`gcprSubmissionTableData.${submissionIndex}.approverDetails.${index}.status`, value, {
          shouldDirty: true,
        })
        break
      case 'gcprSubmission':
        methods.setValue(`gcprSubmissionTableData.${submissionIndex}.status`, value, {
          shouldDirty: true,
        })
        break
      case 'prSubmissionApprover':
        methods.setValue(`prSubmissionTableData.${submissionIndex}.approverDetails.${index}.status`, value, {
          shouldDirty: true,
        })
        break
      case 'prSubmission':
        methods.setValue(`prSubmissionTableData.${submissionIndex}.status`, value, {
          shouldDirty: true,
        })
        break
    }
  }

  return (
    <Modal isOpen={show} onClose={closeModal}>
      <ModalOverlay />
      <ModalContent maxW="50%">
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)}>
            <ModalHeader>
              Edit Offer: [{offerDetail?.quotationNumber || ''}]{offerDetail?.projectName || ''}
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Select
                name="status"
                label="Status"
                isRow
                isRequired={false}
                options={Object.values({ ...OfferQuotationStatus, ...OfferBookingStatus }).map((status) => {
                  return {
                    value: status,
                    label: status,
                  }
                })}
              />

              {Array.isArray(contributorTableData) && contributorTableData.length > 0 && (
                <>
                  <SectionHeader title="Core Contributor Status"></SectionHeader>
                  <EditableTable
                    columnDef={columnDef}
                    data={contributorTableData}
                    showFooter={false}
                    disabled={false}
                    updateData={(data) => updateStatus({ ...data, type: 'contributor' })}
                    tableWidth="100%"
                  />
                </>
              )}

              {Array.isArray(optionalContributorTableData) && optionalContributorTableData.length > 0 && (
                <>
                  <SectionHeader title="Optional Contributor Status"></SectionHeader>
                  <EditableTable
                    columnDef={columnDef}
                    data={optionalContributorTableData}
                    showFooter={false}
                    disabled={false}
                    updateData={(data) => updateStatus({ ...data, type: 'optionalContributor' })}
                    tableWidth="100%"
                  />
                </>
              )}

              {Array.isArray(quotationApproverTableData) && quotationApproverTableData.length > 0 && (
                <>
                  <SectionHeader title="Approver Status"></SectionHeader>
                  <EditableTable
                    columnDef={columnDef}
                    data={quotationApproverTableData}
                    showFooter={false}
                    disabled={false}
                    updateData={(data) => updateStatus({ ...data, type: 'quotationApprover' })}
                    tableWidth="100%"
                  />
                </>
              )}

              {Array.isArray(bookingApproverTableData) && bookingApproverTableData.length > 0 && (
                <>
                  <SectionHeader title="Booking Approver Status"></SectionHeader>
                  <EditableTable
                    columnDef={columnDef}
                    data={bookingApproverTableData}
                    showFooter={false}
                    disabled={false}
                    updateData={(data) => updateStatus({ ...data, type: 'bookingApprover' })}
                    tableWidth="100%"
                  />
                </>
              )}

              {Array.isArray(gcprSubmissionTableData) && gcprSubmissionTableData.length > 0 && (
                <>
                  <SectionHeader title="GCPR"></SectionHeader>
                  <Tabs isLazy variant="enclosed">
                    <TabList overflowX="auto" overflowY="hidden">
                      {gcprSubmissionTableData.map((submission) => (
                        <Tab key={submission.label} whiteSpace="nowrap">
                          {submission.label}
                        </Tab>
                      ))}
                    </TabList>

                    <TabPanels>
                      {gcprSubmissionTableData.map((submission) => (
                        <TabPanel key={`${submission.label}-${submission.submissionIndex}`} px={0}>
                          <Flex mb={4} justifyContent="space-between" alignItems="center">
                            <SectionHeader title="GCPR Submission Status" hasDivider={false} />
                            <ChakraSelect
                              value={submission.status || GCPRStatus.DRAFT}
                              onChange={({ target: { value: newValue } }) => {
                                updateStatus({
                                  type: 'gcprSubmission',
                                  submissionIndex: submission.submissionIndex,
                                  value: newValue,
                                })
                              }}
                              maxW="250px"
                            >
                              {Object.values(GCPRStatus).map((status) => {
                                return (
                                  <option key={status} value={status}>
                                    {status}
                                  </option>
                                )
                              })}
                            </ChakraSelect>
                          </Flex>
                          <EditableTable
                            columnDef={columnDef}
                            data={submission.approverDetails}
                            showFooter={false}
                            disabled={false}
                            updateData={(data) =>
                              updateStatus({
                                ...data,
                                type: 'GCPRApproval',
                                submissionIndex: submission.submissionIndex,
                              })
                            }
                            tableWidth="100%"
                          />
                        </TabPanel>
                      ))}
                    </TabPanels>
                  </Tabs>
                </>
              )}

              {Array.isArray(prSubmissionTableData) && prSubmissionTableData.length > 0 && (
                <>
                  <SectionHeader title="PR Submission Approver Status"></SectionHeader>
                  <Tabs isLazy variant="enclosed">
                    <TabList overflowX="auto" overflowY="hidden">
                      {prSubmissionTableData.map((submission) => (
                        <Tab
                          key={`${submission.costBudgetIndex}-${submission.vendorSelectionIndex}-${submission.submissionIndex}`}
                          whiteSpace="nowrap"
                        >
                          {submission.label}
                        </Tab>
                      ))}
                    </TabList>

                    <TabPanels>
                      {prSubmissionTableData.map((submission) => (
                        <TabPanel
                          key={`${submission.costBudgetIndex}-${submission.vendorSelectionIndex}-${submission.submissionIndex}-panel`}
                          px={0}
                        >
                          <Flex mb={4} justifyContent="space-between" alignItems="center">
                            <SectionHeader title="PR Submission Status" hasDivider={false} />
                            <ChakraSelect
                              value={submission.status || PRStatus.DRAFT}
                              onChange={({ target: { value: newValue } }) => {
                                const submissionTableIndex = prSubmissionTableData.findIndex(
                                  (item) =>
                                    item.costBudgetIndex === submission.costBudgetIndex &&
                                    item.vendorSelectionIndex === submission.vendorSelectionIndex &&
                                    item.submissionIndex === submission.submissionIndex
                                )

                                if (submissionTableIndex < 0) {
                                  return
                                }

                                updateStatus({
                                  type: 'prSubmission',
                                  submissionIndex: submissionTableIndex,
                                  value: newValue,
                                })
                              }}
                              maxW="250px"
                            >
                              {Object.values(PRStatus).map((status) => {
                                return (
                                  <option key={status} value={status}>
                                    {status}
                                  </option>
                                )
                              })}
                            </ChakraSelect>
                          </Flex>

                          <EditableTable
                            columnDef={columnDef}
                            data={submission.approverDetails}
                            showFooter={false}
                            disabled={false}
                            updateData={(data) =>
                              updateStatus({
                                ...data,
                                type: 'prSubmissionApprover',
                                submissionIndex: prSubmissionTableData.findIndex(
                                  (item) =>
                                    item.costBudgetIndex === submission.costBudgetIndex &&
                                    item.vendorSelectionIndex === submission.vendorSelectionIndex &&
                                    item.submissionIndex === submission.submissionIndex
                                ),
                              })
                            }
                            tableWidth="100%"
                          />
                        </TabPanel>
                      ))}
                    </TabPanels>
                  </Tabs>
                </>
              )}
            </ModalBody>

            <ModalFooter>
              <HStack>
                <Button colorScheme="green" type="submit">
                  Save
                </Button>

                <Button colorScheme="gray" onClick={closeModal}>
                  Close
                </Button>
              </HStack>
            </ModalFooter>
          </form>
        </FormProvider>
      </ModalContent>
    </Modal>
  )
}

export default AdminEditOfferModal
